import type { Point } from './grid.js';
import { streamRng, type Rng } from './rng.js';

/**
 * Мусорской патруль — чистая функция от сида, дня и игровой минуты.
 * В сейве его нет: положение выводится, а не хранится, поэтому один и тот же
 * день на любой машине выглядит одинаково и воспроизводится по репорту.
 *
 * Патруль ходит по ломаной туда и обратно: маршруты авторятся как линии,
 * а не замкнутые кольца — так их проще размечать в зонах.
 */

export interface PatrolRoute {
  route: Point[];
}

export interface Patrol {
  id: string;
  /** Дробные координаты: патруль стоит между клетками. */
  x: number;
  y: number;
  /** Радиус внимания в клетках на текущий момент. */
  attention: number;
  /** Ищет тебя, а не гуляет: палево перешагнуло порог. */
  hunting: boolean;
}

/** Патруль неспешный: три игровые минуты на клетку против одной у пацана. */
export const PATROL_MINUTES_PER_TILE = 3;

const BASE_ATTENTION = 4;
const HUNT_HEAT = 60;
const RAID_HEAT = 80;

function polylineLength(route: readonly Point[]): number {
  let total = 0;
  for (let i = 1; i < route.length; i++) {
    total += Math.hypot(route[i]!.x - route[i - 1]!.x, route[i]!.y - route[i - 1]!.y);
  }
  return total;
}

/** Точка на ломаной на расстоянии distance от начала. */
function pointAt(route: readonly Point[], distance: number): Point {
  let left = distance;
  for (let i = 1; i < route.length; i++) {
    const from = route[i - 1]!;
    const to = route[i]!;
    const segment = Math.hypot(to.x - from.x, to.y - from.y);
    if (left <= segment) {
      const t = segment === 0 ? 0 : left / segment;
      return { x: from.x + (to.x - from.x) * t, y: from.y + (to.y - from.y) * t };
    }
    left -= segment;
  }
  return route.at(-1) ?? { x: 0, y: 0 };
}

/**
 * Насколько далеко патруль замечает: приметная тачка и малиновый пиджак
 * работают против тебя, а охота удваивает радиус.
 */
export function attentionRadius(opts: { heat: number; flashiness?: number }): number {
  const { heat, flashiness = 0 } = opts;
  const hunting = heat >= HUNT_HEAT;
  return (BASE_ATTENTION + flashiness) * (hunting ? 2 : 1);
}

/** Где патрули в эту минуту этого дня. */
export function patrolsAt(
  seed: string,
  day: number,
  minute: number,
  zoneId: string,
  routes: readonly PatrolRoute[],
  heat = 0,
  flashiness = 0,
): Patrol[] {
  const attention = attentionRadius({ heat, flashiness });
  const hunting = heat >= HUNT_HEAT;

  return routes.flatMap((entry, index) => {
    if (entry.route.length < 2) return [];
    const length = polylineLength(entry.route);
    if (length === 0) return [];

    // Смещение на день: маршрут тот же, но патруль стоит не там, где вчера.
    const offset = streamRng(seed, `patrol:${zoneId}:${index}`, day).next() * length * 2;
    const travelled = minute / PATROL_MINUTES_PER_TILE + offset;
    const phase = travelled % (length * 2);
    const distance = phase <= length ? phase : length * 2 - phase;
    const at = pointAt(entry.route, distance);

    return [{ id: `${zoneId}:${index}`, x: at.x, y: at.y, attention, hunting }];
  });
}

/** Видит ли хоть один патруль эту клетку. */
export function watchedBy(patrols: readonly Patrol[], tile: Point): Patrol | null {
  for (const patrol of patrols) {
    if (Math.hypot(patrol.x - tile.x, patrol.y - tile.y) <= patrol.attention) return patrol;
  }
  return null;
}

/** Грязное дело на глазах у патруля стоит втрое дороже по палеву. */
export const WATCHED_HEAT_FACTOR = 3;

/**
 * Шанс облавы прямо сейчас: пока палево ниже порога охоты — ноль,
 * дальше растёт линейно. При максимуме поймают почти наверняка.
 */
export function raidChance(heat: number): number {
  if (heat < HUNT_HEAT) return 0;
  return Math.min(0.9, (heat - HUNT_HEAT) / 40);
}

/** Ночью решается отдельно: с палевом за восемьдесят до утра не доживёшь. */
export function nightRaid(heat: number, rng: Rng): boolean {
  if (heat < RAID_HEAT) return false;
  return rng.chance(Math.min(0.9, (heat - RAID_HEAT) / 20 + 0.4));
}

export const HEAT_THRESHOLDS = { hunt: HUNT_HEAT, raid: RAID_HEAT };
